'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@workspace/ui';
import { trackAuthAction } from '@workspace/analytics/posthog/events';
import { CheckCircle2, ChevronLeft } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { forgotPassword } from '@/actions/mixed/auth';
import { DontHaveAnAccountYet } from '@/components/auth/dont-have-an-account-yet';

const forgotPasswordSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email.' }),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPasswordForm() {
  const router = useRouter();

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
    mode: 'onTouched',
  });

  async function onSubmit(values: z.infer<typeof forgotPasswordSchema>) {
    try {
      setIsLoading(true);

      await forgotPassword(values.email);

      trackAuthAction('forgot_password_requested', { result: 'success' });

      // Show success card instead of form
      setIsSuccess(true);
      form.reset();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send reset link';
      toast.error(errorMessage);
      trackAuthAction('forgot_password_requested', {
        result: 'failure',
        reason_code: 'request_failed',
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      {isSuccess ? (
        <Card className="mx-auto mt-24 w-[335px] bg-neutral-100 sm:w-[478px] md:mt-0">
          <div className="flex flex-col gap-7">
            <div className="flex items-center gap-4">
              <CheckCircle2 className="h-16 w-16 text-green-600" />
            </div>

            <div className="flex flex-col gap-4">
              <p className="text-2xl leading-tight font-bold text-black sm:text-4xl dark:text-neutral-100">
                A password reset link has been sent to your email address.
              </p>
              <p className="text-grey-700 dark:text-grey-400 text-sm">
                Follow the instructions to set a new password.
              </p>
              <Button variant="primary" className="mt-2" onClick={() => router.push('/login')}>
                Continue to login
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="mx-auto mt-24 w-[335px] bg-neutral-100 sm:w-[478px] md:mt-0">
          <div className="flex flex-col gap-7">
            <div className="flex flex-col gap-2">
              <Link href="/login">
                <div className="inline-flex h-10 items-center justify-center gap-2 rounded-[60px] py-3">
                  <div className="relative h-4 w-4 overflow-hidden">
                    <ChevronLeft
                      className="text-neutral-200 dark:text-neutral-100"
                      size={16}
                      strokeWidth={1.5}
                    />
                  </div>
                  <p className="justify-center text-sm leading-none font-medium text-neutral-200 dark:text-neutral-100">
                    Back
                  </p>
                </div>
              </Link>

              <p className="justify-center text-2xl font-bold text-black sm:text-4xl dark:text-neutral-100">
                Forgot password
              </p>
            </div>

            <div className="flex flex-col gap-4">
              <p className="text-grey-700 dark:text-grey-400 justify-start self-stretch text-sm">
                Enter your email and we will send you a link to reset your password.
              </p>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-7">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="justify-start text-sm leading-none font-medium text-neutral-200">
                          Email
                        </FormLabel>
                        <FormControl>
                          <Input
                            autoComplete="email"
                            type="email"
                            aria-invalid={form.formState.errors.email ? true : undefined}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex flex-col gap-7">
                    <Button
                      className="gap-6"
                      type="submit"
                      disabled={!form.formState.isValid}
                      isLoading={isLoading}
                      loadingText={'Sending'}
                    >
                      Submit
                    </Button>

                    <DontHaveAnAccountYet />
                  </div>
                </form>
              </Form>
            </div>
          </div>
        </Card>
      )}
    </>
  );
}
